import { message, Modal } from 'antd';

import { useRemoveRelatedTermsMutation } from '../../../../graphql/glossaryTerm.generated';
import { useRemoveRelatedNodesMutation } from '../../../../graphql/glossaryNode.generated';
import { useRemoveTypeOfRelationsMutation } from '../../../../graphql/mutations.generated';
import {
    NodeRelationshipType,
    RelatedNodesInput,
    RelatedTermsInput,
    TermRelationshipType,
} from '../../../../types.generated';

import { getRelationshipType, GLOSSARY_NODE } from '../utils';
import { RelatedTermTypes } from '../../shared/components/styled/search/types';
import { useEntityData, useRefetch } from '../../shared/EntityContext';
import { useTranslation } from '../../../../i18n/useTranslation';

function useRemoveRelatedEntity(
    termUrn: string,
    isInverse: boolean,
    displayName: string,
    relationshipType: string,
    customRelationTypeUrn?: string,
) {
    const { urn } = useEntityData();
    const { t } = useTranslation();

    const refetch = useRefetch();
    const [removeTypeOfRelations] = useRemoveTypeOfRelationsMutation();
    const [removeRelatedNodes] = useRemoveRelatedNodesMutation();
    const [removeRelatedTerms] = useRemoveRelatedTermsMutation();

    // Helper to check if a URN is a GlossaryNode
    const isNodeUrn = (entityUrn: string) => entityUrn.includes(GLOSSARY_NODE);

    // Helper to get the NodeRelationshipType from RelatedTermTypes
    const getNodeRelationshipType = (): NodeRelationshipType => {
        if (customRelationTypeUrn) {
            return NodeRelationshipType.Custom;
        }
        return NodeRelationshipType.TypeOf;
    };

    function handleRemoveRelatedEntity() {
        // Check if the relationship is TypeOf
        const isTypeOf = [RelatedTermTypes.typeOfRelations, RelatedTermTypes.isTypeOf].includes(
            relationshipType as RelatedTermTypes,
        );

        // Check if the related entity (termUrn) is a Node
        const isTargetNode = isNodeUrn(termUrn);

        /**
         * Determine which mutation to use:
         * - removeTypeOfRelations: for Type Of relationships
         * - removeRelatedNodes: when target is a Node (NN or TN)
         * - removeRelatedTerms: when target is a Term (TT or NT)
         */
        const getRemoveMutation = () => {
            if (isTypeOf) {
                return removeTypeOfRelations;
            }
            if (isTargetNode) {
                return removeRelatedNodes;
            }
            return removeRelatedTerms;
        };

        const removeRelation: any = getRemoveMutation();

        // This gets the relationship type to use for the mutation
        const relationshipTypeToUse = getRelationshipType(relationshipType as RelatedTermTypes);

        // Get the NodeRelationshipType for removeRelatedNodes
        const nodeRelationshipType = getNodeRelationshipType();

        /**
         * Builds the input payload based on mutation type:
         * - removeTypeOfRelations: { urn, nodeUrns } - for Type Of relationships
         * - removeRelatedNodes: { urn, nodeUrns, relationshipType, customRelationTypeUrn? } - when target is Node
         * - removeRelatedTerms: { urn, termUrns, relationshipType, customRelationTypeUrn? } - when target is Term
         */
        const buildInput = () => {
            // removeTypeOfRelations
            if (isTypeOf) {
                return isInverse ? { urn: termUrn, nodeUrns: [urn] } : { urn, nodeUrns: [termUrn] };
            }

            // removeRelatedNodes: target is Node (NN or TN)
            if (isTargetNode) {
                const relatedNodesInput: RelatedNodesInput = isInverse
                    ? { urn: termUrn, nodeUrns: [urn], relationshipType: nodeRelationshipType }
                    : { urn, nodeUrns: [termUrn], relationshipType: nodeRelationshipType };

                if (customRelationTypeUrn) {
                    relatedNodesInput.customRelationTypeUrn = customRelationTypeUrn;
                }

                return relatedNodesInput;
            }

            // removeRelatedTerms: target is Term (TT or NT)
            // For custom relations, use TermRelationshipType.Custom as the relationshipType
            const termRelationshipType = customRelationTypeUrn ? TermRelationshipType.Custom : relationshipTypeToUse;

            const relatedTermsInput: RelatedTermsInput = isInverse
                ? { urn: termUrn, termUrns: [urn], relationshipType: termRelationshipType }
                : { urn, termUrns: [termUrn], relationshipType: termRelationshipType };

            if (customRelationTypeUrn) {
                relatedTermsInput.customRelationTypeUrn = customRelationTypeUrn;
            }

            return relatedTermsInput;
        };

        const variables = { input: buildInput() };

        removeRelation({ variables })
            .catch((e) => {
                message.destroy();
                message.error({
                    content: t('Failed to remove: \n {{message}}', { message: e.message || '' }),
                    duration: 3,
                });
            })
            .finally(() => {
                message.loading({
                    content: t('Removing...'),
                    duration: 2,
                });
                setTimeout(() => {
                    refetch();
                    message.success({
                        content: t('Removed Successfully!'),
                        duration: 2,
                    });
                }, 2000);
            });
    }

    function onRemove() {
        Modal.confirm({
            title: t('Remove {{displayName}}', { displayName }),
            content: t('Are you sure you want to remove this?'),
            onOk() {
                handleRemoveRelatedEntity();
            },
            onCancel() {},
            okText: t('Yes'),
            cancelText: t('Cancel'),
            maskClosable: true,
            centered: true,
            closable: true,
            okButtonProps: {
                type: undefined,
                danger: true,
            },
        });
    }

    return { onRemove };
}

export default useRemoveRelatedEntity;
